import { canonicalJsonDigest } from "./canonical-digest.js";
export const CONTINUITY_CONTRACT_VERSION = 1;
export class ContinuityContractError extends Error {
    issue;
    constructor(path, message) {
        super(`${path}: ${message}`);
        this.name = "ContinuityContractError";
        this.issue = { path, message };
    }
}
export const CONTINUITY_EXECUTOR_KIND_VALUES = [
    "dosia-native",
    "codex-cli",
    "claude-code-cli",
    "kimi-cli",
    "grok-cli",
    "human",
];
/** Executor kinds whose sessions are reachable over the ACP stdio protocol (REQ-43). */
export const CONTINUITY_ACP_EXECUTOR_KINDS = ["kimi-cli", "grok-cli"];
/**
 * REQ-47 — structured capability gap of a transfer preflight. `kind` aligns
 * with the Capability Snapshot facets (release/skill/mcp/hook lists), not the
 * org catalog asset kinds. Mechanical entries always carry
 * capabilityRef/kind/severity; `legacy` entries are reason-only migrations of
 * the pre-REQ-47 free-text `capabilityGaps: string[]`.
 */
export const CONTINUITY_CAPABILITY_GAP_KIND_VALUES = ["release", "skill", "mcp_tool", "hook"];
export const CONTINUITY_CAPABILITY_GAP_SEVERITY_VALUES = ["blocking", "degraded"];
export const CONTINUITY_CAPABILITY_GAP_ORIGIN_VALUES = ["mechanical", "manual", "legacy"];
/**
 * REQ-92 — provider-specific ContextPack content that can be stripped on a
 * heterogeneous transfer. `provider` is ContinuityExecutorKind (the family
 * single source on ContinuityExecutorRef.kind; no second family enum).
 *
 * `unannotated` vs `annotated`+empty is type-level distinct: missing
 * annotation must not collapse into "we looked and found nothing".
 */
export const CONTINUITY_STRIPPABLE_SEGMENT_KIND_VALUES = [
    "thinking",
    "encrypted_reasoning",
    "provider_tool_block",
];
/**
 * Live health of a Continuity execution — a volatile projection, not the Attempt ledger.
 *
 * `dead`: the session itself is gone or reached a normal terminal
 * (process exited and the session is finished, or the session record is missing).
 *
 * `unreachable`: the record/files are still visible, but the wake path is
 * irreversibly broken (typical: the bound cwd / worktree was deleted or reaped).
 * Parallel terminal to `dead` — they do not cascade into each other.
 *
 * `unknown` is intentionally absent. Adapters that cannot observe live health
 * must not invent a Runtime row (and must not pretend `idle`).
 */
export const CONTINUITY_SESSION_RUNTIME_HEALTH_VALUES = [
    "launching",
    "working",
    "idle",
    "waiting_auth",
    "waiting_approval",
    "hibernated",
    "dead",
    "unreachable",
];
const MAX_TEXT = 8_192;
const MAX_TITLE = 512;
const MAX_REFERENCE = 512;
const MAX_ITEMS = 100;
function fail(path, message) {
    throw new ContinuityContractError(path, message);
}
function requireObject(value, path) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return fail(path, "must be an object");
    }
    return value;
}
function assertExactKeys(object, path, allowedKeys) {
    const allowed = new Set(allowedKeys);
    for (const key of Object.keys(object)) {
        if (!allowed.has(key))
            fail(`${path}.${key}`, "unknown field");
    }
}
function requireContractVersion(value, path) {
    if (value !== CONTINUITY_CONTRACT_VERSION) {
        return fail(path, `must equal ${CONTINUITY_CONTRACT_VERSION}`);
    }
    return CONTINUITY_CONTRACT_VERSION;
}
function requireString(value, path, maxLength = MAX_TEXT) {
    if (typeof value !== "string"
        || value.trim().length === 0
        || value.length > maxLength
        || /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u.test(value)) {
        return fail(path, `must be a non-empty bounded string (max ${maxLength})`);
    }
    return value;
}
function optionalString(value, path, maxLength = MAX_TEXT) {
    return value === undefined ? undefined : requireString(value, path, maxLength);
}
function requireControlFreeString(value, path, maxLength = MAX_TEXT) {
    const candidate = requireString(value, path, maxLength);
    if (/[\u0000-\u001F\u007F]/u.test(candidate)) {
        fail(path, "must not contain control characters");
    }
    return candidate;
}
function optionalReference(value, path, maxLength = MAX_REFERENCE) {
    return value === undefined ? undefined : requireControlFreeString(value, path, maxLength);
}
function requireId(value, path) {
    const id = requireControlFreeString(value, path, MAX_REFERENCE);
    if (/\s/u.test(id))
        fail(path, "must not contain whitespace");
    return id;
}
function requireInteger(value, path, minimum, maximum) {
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
        return fail(path, `must be an integer from ${minimum} to ${maximum}`);
    }
    return value;
}
function requireLiteralTrue(value, path) {
    if (value !== true)
        return fail(path, "must be true");
    return true;
}
function requireEnum(value, path, values) {
    if (typeof value !== "string" || !values.includes(value)) {
        return fail(path, `must be one of: ${values.join(", ")}`);
    }
    return value;
}
function requireIsoTimestamp(value, path) {
    const timestamp = requireControlFreeString(value, path, 64);
    const milliseconds = Date.parse(timestamp);
    if (!Number.isFinite(milliseconds) || new Date(milliseconds).toISOString() !== timestamp) {
        return fail(path, "must be a canonical ISO-8601 timestamp");
    }
    return timestamp;
}
function assertTimestampOrder(earlier, later, laterPath) {
    if (Date.parse(later) < Date.parse(earlier))
        fail(laterPath, "must not be earlier than the preceding timestamp");
}
function requireDenseArray(value, path) {
    if (!Array.isArray(value) || value.length > MAX_ITEMS) {
        return fail(path, `must be an array with at most ${MAX_ITEMS} items`);
    }
    for (let index = 0; index < value.length; index += 1) {
        if (!Object.hasOwn(value, index))
            fail(`${path}[${index}]`, "must not be a sparse array hole");
    }
    return value;
}
function requireStringArray(value, path, itemMaxLength = MAX_TEXT) {
    const items = requireDenseArray(value, path);
    const result = [];
    for (let index = 0; index < items.length; index += 1) {
        result.push(requireString(items[index], `${path}[${index}]`, itemMaxLength));
    }
    if (new Set(result).size !== result.length)
        fail(path, "must not contain duplicates");
    return result;
}
function requireIdArray(value, path) {
    const items = requireDenseArray(value, path);
    const result = [];
    for (let index = 0; index < items.length; index += 1) {
        result.push(requireId(items[index], `${path}[${index}]`));
    }
    if (new Set(result).size !== result.length)
        fail(path, "must not contain duplicates");
    return result;
}
function requireNonEmptyIdArray(value, path) {
    const result = requireIdArray(value, path);
    if (result.length === 0)
        fail(path, "must contain at least one id");
    return result;
}
function isAbsolutePath(value) {
    return value.startsWith("/") || /^[A-Za-z]:[\\/]/u.test(value) || value.startsWith("\\\\");
}
function requireAbsolutePath(value, path) {
    const candidate = requireControlFreeString(value, path, 4_096);
    if (!isAbsolutePath(candidate))
        fail(path, "must be an absolute path");
    if (candidate.startsWith("~")
        || candidate.split(/[\\/]/u).some((segment) => segment === ".." || segment === "~")) {
        fail(path, "must not contain parent or home-directory traversal");
    }
    return candidate;
}
function requireWorkspaceRelativePath(value, path) {
    const candidate = requireControlFreeString(value, path, 4_096);
    if (isAbsolutePath(candidate)
        || candidate.startsWith("~")
        || candidate.split(/[\\/]/u).some((segment) => segment === ".." || segment === "~")) {
        fail(path, "must be workspace-relative without traversal");
    }
    return candidate;
}
function requireSafeHttpUrl(value, path) {
    const candidate = requireControlFreeString(value, path, 4_096);
    let parsed;
    try {
        parsed = new URL(candidate);
    }
    catch {
        return fail(path, "must be an absolute HTTP(S) URL");
    }
    if ((parsed.protocol !== "http:" && parsed.protocol !== "https:") || parsed.username !== "" || parsed.password !== "") {
        fail(path, "must be HTTP(S) and must not contain credentials");
    }
    return candidate;
}
function requireSha256Ref(value, path) {
    const candidate = requireControlFreeString(value, path, 74);
    if (!/^sha256:v1:[a-f0-9]{64}$/u.test(candidate)) {
        fail(path, "must use the versioned sha256:v1:<64 lowercase hex> shape");
    }
    return candidate;
}
function parsePrivacyScope(value, path) {
    return requireEnum(value, path, ["personal", "project", "role", "organization"]);
}
function parseTransitionMarker(value, path) {
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["eventId", "fingerprint", "toStatus"]);
    return {
        eventId: requireId(object.eventId, `${path}.eventId`),
        fingerprint: requireSha256Ref(object.fingerprint, `${path}.fingerprint`),
        toStatus: requireControlFreeString(object.toStatus, `${path}.toStatus`, 64),
    };
}
function optionalTransitionMarker(value, path) {
    return value === undefined ? undefined : parseTransitionMarker(value, path);
}
function parseMutableTimes(object, path) {
    const createdAt = requireIsoTimestamp(object.createdAt, `${path}.createdAt`);
    const updatedAt = requireIsoTimestamp(object.updatedAt, `${path}.updatedAt`);
    assertTimestampOrder(createdAt, updatedAt, `${path}.updatedAt`);
    return {
        createdAt,
        updatedAt,
        version: requireInteger(object.version, `${path}.version`, 1, Number.MAX_SAFE_INTEGER),
    };
}
export function parseContinuityExecutorRef(value) {
    const path = "executorRef";
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["contractVersion", "id", "kind"]);
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        kind: requireEnum(object.kind, `${path}.kind`, CONTINUITY_EXECUTOR_KIND_VALUES),
    };
}
export function buildContinuityExecutorRef(input) {
    return parseContinuityExecutorRef(input);
}
export function parseContinuityAcpResumePlan(value) {
    const path = "acpResumePlan";
    const object = requireObject(value, path);
    const executor = requireEnum(object.executor, `${path}.executor`, CONTINUITY_ACP_EXECUTOR_KINDS);
    if (executor === "kimi-cli") {
        assertExactKeys(object, path, ["executor", "sessionId"]);
        return {
            executor,
            sessionId: requireId(object.sessionId, `${path}.sessionId`),
        };
    }
    assertExactKeys(object, path, ["executor", "sessionId", "cwd"]);
    return {
        executor,
        sessionId: requireId(object.sessionId, `${path}.sessionId`),
        cwd: requireAbsolutePath(object.cwd, `${path}.cwd`),
    };
}
export function buildContinuityAcpResumePlan(input) {
    return parseContinuityAcpResumePlan(input);
}
export function parseContinuityWorkItem(value) {
    const path = "workItem";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "title",
        "objective",
        "projectRef",
        "ownerRef",
        "status",
        "priority",
        "acceptanceCriteria",
        "privacyScope",
        "createdAt",
        "updatedAt",
        "completedAt",
        "acceptanceId",
        "activeAttemptId",
        "lastTransition",
        "version",
    ]);
    const status = requireEnum(object.status, `${path}.status`, ["backlog", "ready", "in_progress", "waiting", "needs_review", "done", "cancelled"]);
    const times = parseMutableTimes(object, path);
    const completedAt = object.completedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.completedAt, `${path}.completedAt`);
    const acceptanceId = object.acceptanceId === undefined
        ? undefined
        : requireId(object.acceptanceId, `${path}.acceptanceId`);
    const terminal = status === "done" || status === "cancelled";
    if (terminal && completedAt === undefined)
        fail(`${path}.completedAt`, "is required for a terminal Work Item");
    if (!terminal && completedAt !== undefined)
        fail(`${path}.completedAt`, "is forbidden before a terminal state");
    if (status === "done" && acceptanceId === undefined)
        fail(`${path}.acceptanceId`, "is required for done");
    if (status !== "done" && acceptanceId !== undefined)
        fail(`${path}.acceptanceId`, "is only valid for done");
    if (completedAt !== undefined) {
        assertTimestampOrder(times.createdAt, completedAt, `${path}.completedAt`);
        assertTimestampOrder(completedAt, times.updatedAt, `${path}.updatedAt`);
    }
    const projectRef = optionalReference(object.projectRef, `${path}.projectRef`);
    const activeAttemptId = object.activeAttemptId === undefined
        ? undefined
        : requireId(object.activeAttemptId, `${path}.activeAttemptId`);
    const lastTransition = optionalTransitionMarker(object.lastTransition, `${path}.lastTransition`);
    if (lastTransition !== undefined && lastTransition.toStatus !== status) {
        fail(`${path}.lastTransition.toStatus`, "must equal the current Work Item status");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        title: requireString(object.title, `${path}.title`, MAX_TITLE),
        objective: requireString(object.objective, `${path}.objective`),
        ...(projectRef === undefined ? {} : { projectRef }),
        ownerRef: requireId(object.ownerRef, `${path}.ownerRef`),
        status,
        priority: requireInteger(object.priority, `${path}.priority`, 0, 100),
        acceptanceCriteria: requireStringArray(object.acceptanceCriteria, `${path}.acceptanceCriteria`),
        privacyScope: parsePrivacyScope(object.privacyScope, `${path}.privacyScope`),
        ...times,
        ...(completedAt === undefined ? {} : { completedAt }),
        ...(acceptanceId === undefined ? {} : { acceptanceId }),
        ...(activeAttemptId === undefined ? {} : { activeAttemptId }),
        ...(lastTransition === undefined ? {} : { lastTransition }),
    };
}
export function buildContinuityWorkItem(input) {
    return parseContinuityWorkItem(input);
}
export function parseContinuityAttempt(value) {
    const path = "attempt";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "executorRef",
        "runtimeSessionRef",
        "cwd",
        "workspaceRef",
        "worktreeRef",
        "branch",
        "status",
        "createdAt",
        "updatedAt",
        "startedAt",
        "endedAt",
        "resumeRef",
        "checkpointRef",
        "resultSummary",
        "failure",
        "capabilitySnapshotRef",
        "recoveryEvidenceId",
        "lastTransition",
        "version",
    ]);
    const status = requireEnum(object.status, `${path}.status`, ["queued", "running", "waiting_auth", "waiting_approval", "interrupted", "succeeded", "failed", "cancelled", "timed_out"]);
    const times = parseMutableTimes(object, path);
    const startedAt = object.startedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.startedAt, `${path}.startedAt`);
    const endedAt = object.endedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.endedAt, `${path}.endedAt`);
    const terminal = status === "succeeded" || status === "failed" || status === "cancelled" || status === "timed_out";
    if (terminal && endedAt === undefined)
        fail(`${path}.endedAt`, "is required for a terminal Attempt");
    if (!terminal && endedAt !== undefined)
        fail(`${path}.endedAt`, "is forbidden before a terminal state");
    if ((status === "running" || status === "interrupted" || terminal) && startedAt === undefined) {
        fail(`${path}.startedAt`, `is required for status ${status}`);
    }
    if (startedAt !== undefined) {
        assertTimestampOrder(times.createdAt, startedAt, `${path}.startedAt`);
        assertTimestampOrder(startedAt, times.updatedAt, `${path}.updatedAt`);
    }
    if (endedAt !== undefined) {
        assertTimestampOrder(startedAt ?? times.createdAt, endedAt, `${path}.endedAt`);
        assertTimestampOrder(endedAt, times.updatedAt, `${path}.updatedAt`);
    }
    const runtimeSessionRef = optionalReference(object.runtimeSessionRef, `${path}.runtimeSessionRef`);
    const workspaceRef = optionalReference(object.workspaceRef, `${path}.workspaceRef`);
    const worktreeRef = object.worktreeRef === undefined
        ? undefined
        : requireAbsolutePath(object.worktreeRef, `${path}.worktreeRef`);
    const branch = optionalReference(object.branch, `${path}.branch`);
    const resumeRef = optionalReference(object.resumeRef, `${path}.resumeRef`);
    const checkpointRef = optionalReference(object.checkpointRef, `${path}.checkpointRef`);
    const resultSummary = optionalString(object.resultSummary, `${path}.resultSummary`);
    const failure = optionalString(object.failure, `${path}.failure`);
    if (resultSummary !== undefined && status !== "succeeded") {
        fail(`${path}.resultSummary`, "is only valid for succeeded");
    }
    if (failure !== undefined && status !== "failed") {
        fail(`${path}.failure`, "is only valid for failed");
    }
    const capabilitySnapshotRef = optionalReference(object.capabilitySnapshotRef, `${path}.capabilitySnapshotRef`);
    const recoveryEvidenceId = object.recoveryEvidenceId === undefined
        ? undefined
        : requireId(object.recoveryEvidenceId, `${path}.recoveryEvidenceId`);
    if (recoveryEvidenceId !== undefined && status !== "succeeded") {
        fail(`${path}.recoveryEvidenceId`, "is only valid for succeeded");
    }
    const lastTransition = optionalTransitionMarker(object.lastTransition, `${path}.lastTransition`);
    if (lastTransition !== undefined && lastTransition.toStatus !== status) {
        fail(`${path}.lastTransition.toStatus`, "must equal the current Attempt status");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        executorRef: parseContinuityExecutorRef(object.executorRef),
        ...(runtimeSessionRef === undefined ? {} : { runtimeSessionRef }),
        cwd: requireAbsolutePath(object.cwd, `${path}.cwd`),
        ...(workspaceRef === undefined ? {} : { workspaceRef }),
        ...(worktreeRef === undefined ? {} : { worktreeRef }),
        ...(branch === undefined ? {} : { branch }),
        status,
        ...times,
        ...(startedAt === undefined ? {} : { startedAt }),
        ...(endedAt === undefined ? {} : { endedAt }),
        ...(resumeRef === undefined ? {} : { resumeRef }),
        ...(checkpointRef === undefined ? {} : { checkpointRef }),
        ...(resultSummary === undefined ? {} : { resultSummary }),
        ...(failure === undefined ? {} : { failure }),
        ...(capabilitySnapshotRef === undefined ? {} : { capabilitySnapshotRef }),
        ...(recoveryEvidenceId === undefined ? {} : { recoveryEvidenceId }),
        ...(lastTransition === undefined ? {} : { lastTransition }),
    };
}
export function buildContinuityAttempt(input) {
    return parseContinuityAttempt(input);
}
function parseContextDecisions(value, path) {
    const entries = requireDenseArray(value, path);
    const result = [];
    for (let index = 0; index < entries.length; index += 1) {
        const entry = entries[index];
        const entryPath = `${path}[${index}]`;
        const object = requireObject(entry, entryPath);
        assertExactKeys(object, entryPath, ["decision", "rationale"]);
        result.push({
            decision: requireString(object.decision, `${entryPath}.decision`),
            rationale: requireString(object.rationale, `${entryPath}.rationale`),
        });
    }
    const identities = result.map(({ decision, rationale }) => `${decision}\u0000${rationale}`);
    if (new Set(identities).size !== identities.length)
        fail(path, "must not contain duplicates");
    return result;
}
export function parseContinuityCapabilityGap(value) {
    const path = "capabilityGap";
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["capabilityRef", "kind", "severity", "reason", "origin"]);
    const origin = requireEnum(object.origin, `${path}.origin`, CONTINUITY_CAPABILITY_GAP_ORIGIN_VALUES);
    const capabilityRef = optionalReference(object.capabilityRef, `${path}.capabilityRef`);
    const kind = object.kind === undefined
        ? undefined
        : requireEnum(object.kind, `${path}.kind`, CONTINUITY_CAPABILITY_GAP_KIND_VALUES);
    const severity = object.severity === undefined
        ? undefined
        : requireEnum(object.severity, `${path}.severity`, CONTINUITY_CAPABILITY_GAP_SEVERITY_VALUES);
    const reason = requireString(object.reason, `${path}.reason`);
    if (origin === "mechanical" && (capabilityRef === undefined || kind === undefined || severity === undefined)) {
        fail(path, "mechanical gaps must carry capabilityRef, kind, and severity");
    }
    return {
        ...(capabilityRef === undefined ? {} : { capabilityRef }),
        ...(kind === undefined ? {} : { kind }),
        ...(severity === undefined ? {} : { severity }),
        reason,
        origin,
    };
}
export function buildContinuityCapabilityGap(input) {
    return parseContinuityCapabilityGap(input);
}
function requireCapabilityGapArray(value, path) {
    const items = requireDenseArray(value, path);
    const result = items.map((item, index) => parseContinuityCapabilityGap(item));
    const identities = result.map((gap) => JSON.stringify([
        gap.capabilityRef ?? "",
        gap.kind ?? "",
        gap.severity ?? "",
        gap.reason,
        gap.origin,
    ]));
    if (new Set(identities).size !== identities.length)
        fail(path, "must not contain duplicates");
    return result;
}
export function parseContinuityCapabilityGapReport(value) {
    const path = "capabilityGapReport";
    const object = requireObject(value, path);
    const status = requireEnum(object.status, `${path}.status`, ["diffed", "undiffable"]);
    if (status === "diffed") {
        assertExactKeys(object, path, ["status", "gaps"]);
        return { status, gaps: requireCapabilityGapArray(object.gaps, `${path}.gaps`) };
    }
    assertExactKeys(object, path, ["status", "reason", "gaps"]);
    return {
        status,
        reason: requireString(object.reason, `${path}.reason`),
        gaps: requireCapabilityGapArray(object.gaps, `${path}.gaps`),
    };
}
export function buildContinuityCapabilityGapReport(input) {
    return parseContinuityCapabilityGapReport(input);
}
function parseStrippableSegmentOrigin(value, path) {
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["messageIndex", "messageId"]);
    const messageId = object.messageId === undefined
        ? undefined
        : requireId(object.messageId, `${path}.messageId`);
    return {
        messageIndex: requireInteger(object.messageIndex, `${path}.messageIndex`, 0, Number.MAX_SAFE_INTEGER),
        ...(messageId === undefined ? {} : { messageId }),
    };
}
export function parseContinuityStrippableSegment(value) {
    const path = "strippableSegment";
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["kind", "provider", "content", "origin", "signature", "toolName"]);
    const signature = optionalString(object.signature, `${path}.signature`);
    const toolName = object.toolName === undefined
        ? undefined
        : requireId(object.toolName, `${path}.toolName`);
    return {
        kind: requireEnum(object.kind, `${path}.kind`, CONTINUITY_STRIPPABLE_SEGMENT_KIND_VALUES),
        provider: requireEnum(object.provider, `${path}.provider`, CONTINUITY_EXECUTOR_KIND_VALUES),
        content: requireString(object.content, `${path}.content`),
        origin: parseStrippableSegmentOrigin(object.origin, `${path}.origin`),
        ...(signature === undefined ? {} : { signature }),
        ...(toolName === undefined ? {} : { toolName }),
    };
}
export function buildContinuityStrippableSegment(input) {
    return parseContinuityStrippableSegment(input);
}
function parseStrippableSegmentArray(value, path) {
    const items = requireDenseArray(value, path);
    const result = items.map((item) => parseContinuityStrippableSegment(item));
    const identities = result.map((segment) => JSON.stringify([
        segment.kind,
        segment.provider,
        segment.origin.messageIndex,
        segment.origin.messageId ?? "",
        segment.content,
        segment.signature ?? "",
        segment.toolName ?? "",
    ]));
    if (new Set(identities).size !== identities.length)
        fail(path, "must not contain duplicates");
    return result;
}
export function parseContinuityStrippableAnnotation(value) {
    const path = "strippableSegments";
    const object = requireObject(value, path);
    const status = requireEnum(object.status, `${path}.status`, ["unannotated", "annotated"]);
    if (status === "unannotated") {
        assertExactKeys(object, path, ["status"]);
        return { status };
    }
    assertExactKeys(object, path, ["status", "segments"]);
    return { status, segments: parseStrippableSegmentArray(object.segments, `${path}.segments`) };
}
export function buildContinuityStrippableAnnotation(input) {
    return parseContinuityStrippableAnnotation(input);
}
function parseStrippableStripCount(value, path) {
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["kind", "provider", "count"]);
    return {
        kind: requireEnum(object.kind, `${path}.kind`, CONTINUITY_STRIPPABLE_SEGMENT_KIND_VALUES),
        provider: requireEnum(object.provider, `${path}.provider`, CONTINUITY_EXECUTOR_KIND_VALUES),
        count: requireInteger(object.count, `${path}.count`, 1, Number.MAX_SAFE_INTEGER),
    };
}
export function parseContinuityStrippableResidue(value) {
    const path = "strippableResidue";
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["stripped", "unannotatedResidueCount"]);
    const strippedItems = requireDenseArray(object.stripped, `${path}.stripped`);
    const stripped = strippedItems.map((item, index) => parseStrippableStripCount(item, `${path}.stripped[${index}]`));
    const identities = stripped.map((entry) => JSON.stringify([entry.kind, entry.provider]));
    if (new Set(identities).size !== identities.length)
        fail(`${path}.stripped`, "must not contain duplicates");
    return {
        stripped,
        unannotatedResidueCount: requireInteger(object.unannotatedResidueCount, `${path}.unannotatedResidueCount`, 0, Number.MAX_SAFE_INTEGER),
    };
}
export function buildContinuityStrippableResidue(input) {
    return parseContinuityStrippableResidue(input);
}
/**
 * REQ-92 consumption: strip segments whose provider family differs from the
 * target executor kind. Input is the typed annotation only — never raw text
 * plus a pattern. Unannotated packs are retained as-is and counted as residue.
 */
export function stripContinuityStrippableSegments(annotation, targetProvider) {
    requireEnum(targetProvider, "targetProvider", CONTINUITY_EXECUTOR_KIND_VALUES);
    if (annotation.status === "unannotated") {
        return {
            retained: { status: "unannotated" },
            residue: { stripped: [], unannotatedResidueCount: 1 },
        };
    }
    const kept = [];
    const counts = new Map();
    for (const segment of annotation.segments) {
        if (segment.provider === targetProvider) {
            kept.push(segment);
            continue;
        }
        const key = `${segment.kind}\0${segment.provider}`;
        const existing = counts.get(key);
        if (existing) {
            existing.count += 1;
        }
        else {
            counts.set(key, { kind: segment.kind, provider: segment.provider, count: 1 });
        }
    }
    return {
        retained: { status: "annotated", segments: kept },
        residue: {
            stripped: [...counts.values()],
            unannotatedResidueCount: 0,
        },
    };
}
export function parseContinuityContextPack(value) {
    const path = "contextPack";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "attemptId",
        "objective",
        "acceptanceCriteria",
        "confirmedFacts",
        "decisions",
        "completedWork",
        "openTodos",
        "blockers",
        "authorizationWaits",
        "cwd",
        "worktreeRef",
        "branch",
        "criticalFiles",
        "artifactIds",
        "verificationStatus",
        "doNotRepeat",
        "capabilityGaps",
        "strippableSegments",
        "sourceExecutorRef",
        "targetExecutorRef",
        "sourceCapabilitySnapshotRef",
        "targetCapabilitySnapshotRef",
        "sourceSessionObservationId",
        "privacyScope",
        "generatedAt",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const times = parseMutableTimes(object, path);
    const generatedAt = requireIsoTimestamp(object.generatedAt, `${path}.generatedAt`);
    assertTimestampOrder(times.createdAt, generatedAt, `${path}.generatedAt`);
    assertTimestampOrder(generatedAt, times.updatedAt, `${path}.updatedAt`);
    const worktreeRef = object.worktreeRef === undefined
        ? undefined
        : requireAbsolutePath(object.worktreeRef, `${path}.worktreeRef`);
    const branch = optionalReference(object.branch, `${path}.branch`);
    const criticalFiles = requireStringArray(object.criticalFiles, `${path}.criticalFiles`, 4_096)
        .map((file, index) => requireWorkspaceRelativePath(file, `${path}.criticalFiles[${index}]`));
    const sourceCapabilitySnapshotRef = optionalReference(object.sourceCapabilitySnapshotRef, `${path}.sourceCapabilitySnapshotRef`);
    const targetCapabilitySnapshotRef = optionalReference(object.targetCapabilitySnapshotRef, `${path}.targetCapabilitySnapshotRef`);
    const sourceSessionObservationId = object.sourceSessionObservationId === undefined
        ? undefined
        : requireId(object.sourceSessionObservationId, `${path}.sourceSessionObservationId`);
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        attemptId: requireId(object.attemptId, `${path}.attemptId`),
        objective: requireString(object.objective, `${path}.objective`),
        acceptanceCriteria: requireStringArray(object.acceptanceCriteria, `${path}.acceptanceCriteria`),
        confirmedFacts: requireStringArray(object.confirmedFacts, `${path}.confirmedFacts`),
        decisions: parseContextDecisions(object.decisions, `${path}.decisions`),
        completedWork: requireStringArray(object.completedWork, `${path}.completedWork`),
        openTodos: requireStringArray(object.openTodos, `${path}.openTodos`),
        blockers: requireStringArray(object.blockers, `${path}.blockers`),
        authorizationWaits: requireStringArray(object.authorizationWaits, `${path}.authorizationWaits`),
        cwd: requireAbsolutePath(object.cwd, `${path}.cwd`),
        ...(worktreeRef === undefined ? {} : { worktreeRef }),
        ...(branch === undefined ? {} : { branch }),
        criticalFiles,
        artifactIds: requireIdArray(object.artifactIds, `${path}.artifactIds`),
        verificationStatus: requireEnum(object.verificationStatus, `${path}.verificationStatus`, ["unverified", "passed", "failed", "partial", "blocked"]),
        doNotRepeat: requireStringArray(object.doNotRepeat, `${path}.doNotRepeat`),
        capabilityGaps: parseContinuityCapabilityGapReport(object.capabilityGaps),
        strippableSegments: parseContinuityStrippableAnnotation(object.strippableSegments),
        sourceExecutorRef: parseContinuityExecutorRef(object.sourceExecutorRef),
        targetExecutorRef: parseContinuityExecutorRef(object.targetExecutorRef),
        ...(sourceCapabilitySnapshotRef === undefined ? {} : { sourceCapabilitySnapshotRef }),
        ...(targetCapabilitySnapshotRef === undefined ? {} : { targetCapabilitySnapshotRef }),
        ...(sourceSessionObservationId === undefined ? {} : { sourceSessionObservationId }),
        privacyScope: parsePrivacyScope(object.privacyScope, `${path}.privacyScope`),
        generatedAt,
        ...times,
    };
}
export function buildContinuityContextPack(input) {
    return parseContinuityContextPack(input);
}
/**
 * Runtime guard for raw model output (invariant #7 — never `as`). Malformed
 * input returns `undefined` so the caller can map it to `invalid_output`; only
 * `ContinuityContractError` is swallowed, anything else rethrows.
 */
export function parseContextPackDraft(value) {
    const path = "contextPackDraft";
    try {
        const object = requireObject(value, path);
        assertExactKeys(object, path, ["objective", "confirmedFacts", "decisions", "openTodos"]);
        return {
            objective: requireString(object.objective, `${path}.objective`),
            confirmedFacts: requireStringArray(object.confirmedFacts, `${path}.confirmedFacts`),
            decisions: parseContextDecisions(object.decisions, `${path}.decisions`),
            openTodos: requireStringArray(object.openTodos, `${path}.openTodos`),
        };
    }
    catch (error) {
        if (error instanceof ContinuityContractError)
            return undefined;
        throw error;
    }
}
export function parseContinuityArtifact(value) {
    const path = "artifact";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "attemptId",
        "title",
        "kind",
        "locator",
        "mediaType",
        "privacyScope",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const kind = requireEnum(object.kind, `${path}.kind`, ["file", "url", "dataset", "code_change", "report", "other"]);
    const locator = kind === "file"
        ? requireAbsolutePath(object.locator, `${path}.locator`)
        : kind === "url"
            ? requireSafeHttpUrl(object.locator, `${path}.locator`)
            : requireControlFreeString(object.locator, `${path}.locator`, 4_096);
    const mediaType = optionalReference(object.mediaType, `${path}.mediaType`, 256);
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        attemptId: requireId(object.attemptId, `${path}.attemptId`),
        title: requireString(object.title, `${path}.title`, MAX_TITLE),
        kind,
        locator,
        ...(mediaType === undefined ? {} : { mediaType }),
        privacyScope: parsePrivacyScope(object.privacyScope, `${path}.privacyScope`),
        ...parseMutableTimes(object, path),
    };
}
export function buildContinuityArtifact(input) {
    return parseContinuityArtifact(input);
}
export function parseContinuityEvidence(value) {
    const path = "evidence";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "attemptId",
        "kind",
        "summary",
        "outcome",
        "authority",
        "provenanceRef",
        "artifactIds",
        "privacyScope",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const provenanceRef = optionalReference(object.provenanceRef, `${path}.provenanceRef`);
    const authority = requireEnum(object.authority, `${path}.authority`, ["authoritative", "supporting"]);
    if (authority === "authoritative" && provenanceRef === undefined) {
        fail(`${path}.provenanceRef`, "is required for authoritative Evidence");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        attemptId: requireId(object.attemptId, `${path}.attemptId`),
        kind: requireEnum(object.kind, `${path}.kind`, ["test", "screenshot", "tool_result", "approval", "receipt", "business_data", "other"]),
        summary: requireString(object.summary, `${path}.summary`),
        outcome: requireEnum(object.outcome, `${path}.outcome`, ["passed", "failed", "partial", "unverified"]),
        authority,
        ...(provenanceRef === undefined ? {} : { provenanceRef }),
        artifactIds: requireIdArray(object.artifactIds, `${path}.artifactIds`),
        privacyScope: parsePrivacyScope(object.privacyScope, `${path}.privacyScope`),
        ...parseMutableTimes(object, path),
    };
}
export function buildContinuityEvidence(input) {
    return parseContinuityEvidence(input);
}
/** Pure in-memory relation validation only; this does not claim FK, authority, or repository validation. */
export function validateContinuityAttemptBoundEntityRelation(input) {
    const { entity, workItem, attempt } = input;
    const ids = [entity.workItemId, entity.attemptId, workItem.id, attempt.id, attempt.workItemId];
    if (ids.some((id) => typeof id !== "string" || id.length === 0)
        || entity.workItemId !== workItem.id
        || entity.attemptId !== attempt.id
        || attempt.workItemId !== workItem.id) {
        return {
            status: "relation_mismatch",
            reason: "entity, Attempt, and Work Item do not share one relation",
        };
    }
    return { status: "valid" };
}
export function parseContinuityTransfer(value) {
    const path = "transfer";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "sourceAttemptId",
        "targetExecutorRef",
        "contextPackId",
        "status",
        "targetAttemptId",
        "capabilityGaps",
        "strippableResidue",
        "authorizationNotes",
        "risks",
        "reason",
        "idempotencyKey",
        "createdAt",
        "updatedAt",
        "lastTransition",
        "version",
    ]);
    const status = requireEnum(object.status, `${path}.status`, ["pending", "accepted", "rejected", "failed"]);
    const targetAttemptId = object.targetAttemptId === undefined
        ? undefined
        : requireId(object.targetAttemptId, `${path}.targetAttemptId`);
    const reason = optionalString(object.reason, `${path}.reason`);
    if (status === "accepted" && targetAttemptId === undefined) {
        fail(`${path}.targetAttemptId`, "is required for an accepted Transfer");
    }
    if (status !== "accepted" && targetAttemptId !== undefined) {
        fail(`${path}.targetAttemptId`, "is only valid for an accepted Transfer");
    }
    if ((status === "rejected" || status === "failed") && reason === undefined) {
        fail(`${path}.reason`, `is required for status ${status}`);
    }
    if ((status === "pending" || status === "accepted") && reason !== undefined) {
        fail(`${path}.reason`, `is forbidden for status ${status}`);
    }
    const lastTransition = optionalTransitionMarker(object.lastTransition, `${path}.lastTransition`);
    if (lastTransition !== undefined && lastTransition.toStatus !== status) {
        fail(`${path}.lastTransition.toStatus`, "must equal the current Transfer status");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        sourceAttemptId: requireId(object.sourceAttemptId, `${path}.sourceAttemptId`),
        targetExecutorRef: parseContinuityExecutorRef(object.targetExecutorRef),
        contextPackId: requireId(object.contextPackId, `${path}.contextPackId`),
        status,
        ...(targetAttemptId === undefined ? {} : { targetAttemptId }),
        capabilityGaps: parseContinuityCapabilityGapReport(object.capabilityGaps),
        strippableResidue: parseContinuityStrippableResidue(object.strippableResidue),
        authorizationNotes: requireStringArray(object.authorizationNotes, `${path}.authorizationNotes`),
        risks: requireStringArray(object.risks, `${path}.risks`),
        ...(reason === undefined ? {} : { reason }),
        idempotencyKey: requireId(object.idempotencyKey, `${path}.idempotencyKey`),
        ...parseMutableTimes(object, path),
        ...(lastTransition === undefined ? {} : { lastTransition }),
    };
}
export function buildContinuityTransfer(input) {
    return parseContinuityTransfer(input);
}
export function parseContinuitySupervision(value) {
    const path = "supervision";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "scope",
        "attemptId",
        "declaredBy",
        "declaredAt",
        "releasedAt",
        "releaseReason",
        "releaseEventId",
        "idempotencyKey",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const scope = requireEnum(object.scope, `${path}.scope`, ["attempt", "children"]);
    const declaredAt = requireIsoTimestamp(object.declaredAt, `${path}.declaredAt`);
    const releasedAt = object.releasedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.releasedAt, `${path}.releasedAt`);
    const releaseReason = object.releaseReason === undefined
        ? undefined
        : requireEnum(object.releaseReason, `${path}.releaseReason`, ["worker_terminal_event", "human_withdrawal"]);
    const releaseEventId = object.releaseEventId === undefined
        ? undefined
        : requireId(object.releaseEventId, `${path}.releaseEventId`);
    if (releasedAt === undefined && (releaseReason !== undefined || releaseEventId !== undefined)) {
        fail(`${path}.releasedAt`, "is required when release authority is present");
    }
    if (releasedAt !== undefined && (releaseReason === undefined || releaseEventId === undefined)) {
        fail(`${path}.releaseReason`, "release authority must be complete");
    }
    if (releasedAt !== undefined) {
        assertTimestampOrder(declaredAt, releasedAt, `${path}.releasedAt`);
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        scope,
        attemptId: requireId(object.attemptId, `${path}.attemptId`),
        declaredBy: requireId(object.declaredBy, `${path}.declaredBy`),
        declaredAt,
        ...(releasedAt === undefined ? {} : { releasedAt }),
        ...(releaseReason === undefined ? {} : { releaseReason }),
        ...(releaseEventId === undefined ? {} : { releaseEventId }),
        idempotencyKey: requireId(object.idempotencyKey, `${path}.idempotencyKey`),
        ...parseMutableTimes(object, path),
    };
}
export function buildContinuitySupervision(input) {
    return parseContinuitySupervision(input);
}
export function parseContinuityAcceptance(value) {
    const path = "acceptance";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "workItemId",
        "attemptId",
        "decision",
        "actor",
        "evidenceIds",
        "reason",
        "targetExecutorRef",
        "knowledgeCandidateIds",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const decision = requireEnum(object.decision, `${path}.decision`, ["accept", "reject", "retry_same_executor", "transfer_other_executor", "cancel", "promote_knowledge"]);
    const reason = optionalString(object.reason, `${path}.reason`);
    const targetExecutorRef = object.targetExecutorRef === undefined
        ? undefined
        : parseContinuityExecutorRef(object.targetExecutorRef);
    const knowledgeCandidateIds = object.knowledgeCandidateIds === undefined
        ? undefined
        : requireNonEmptyIdArray(object.knowledgeCandidateIds, `${path}.knowledgeCandidateIds`);
    const actorObject = requireObject(object.actor, `${path}.actor`);
    assertExactKeys(actorObject, `${path}.actor`, ["kind", "id"]);
    if (actorObject.kind !== "human")
        fail(`${path}.actor.kind`, "must be human");
    const common = {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        attemptId: requireId(object.attemptId, `${path}.attemptId`),
        actor: { kind: "human", id: requireId(actorObject.id, `${path}.actor.id`) },
        evidenceIds: requireIdArray(object.evidenceIds, `${path}.evidenceIds`),
        ...parseMutableTimes(object, path),
    };
    if (decision === "reject") {
        if (reason === undefined)
            fail(`${path}.reason`, "is required for reject");
        if (targetExecutorRef !== undefined)
            fail(`${path}.targetExecutorRef`, "is only valid for transfer_other_executor");
        if (knowledgeCandidateIds !== undefined)
            fail(`${path}.knowledgeCandidateIds`, "is only valid for promote_knowledge");
        return { ...common, decision, reason };
    }
    if (reason !== undefined)
        fail(`${path}.reason`, "is only valid for reject");
    if (decision === "transfer_other_executor") {
        if (targetExecutorRef === undefined)
            fail(`${path}.targetExecutorRef`, "is required for transfer_other_executor");
        if (knowledgeCandidateIds !== undefined)
            fail(`${path}.knowledgeCandidateIds`, "is only valid for promote_knowledge");
        return { ...common, decision, targetExecutorRef };
    }
    if (targetExecutorRef !== undefined)
        fail(`${path}.targetExecutorRef`, "is only valid for transfer_other_executor");
    if (decision === "promote_knowledge") {
        if (knowledgeCandidateIds === undefined)
            fail(`${path}.knowledgeCandidateIds`, "is required for promote_knowledge");
        return { ...common, decision, knowledgeCandidateIds };
    }
    if (knowledgeCandidateIds !== undefined)
        fail(`${path}.knowledgeCandidateIds`, "is only valid for promote_knowledge");
    return { ...common, decision };
}
export function buildContinuityAcceptance(input) {
    return parseContinuityAcceptance(input);
}
function parseFacetCompleteness(value, path) {
    const object = requireObject(value, path);
    assertExactKeys(object, path, ["time", "project", "model", "status", "goal", "result"]);
    const values = ["complete", "partial", "unsupported"];
    return {
        time: requireEnum(object.time, `${path}.time`, values),
        project: requireEnum(object.project, `${path}.project`, values),
        model: requireEnum(object.model, `${path}.model`, values),
        status: requireEnum(object.status, `${path}.status`, values),
        goal: requireEnum(object.goal, `${path}.goal`, values),
        result: requireEnum(object.result, `${path}.result`, values),
    };
}
export function parseContinuitySessionObservation(value) {
    const path = "sessionObservation";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "source",
        "sessionKey",
        "fingerprint",
        "localOnly",
        "observedAt",
        "status",
        "resultExistence",
        "resultFinality",
        "goalAvailability",
        "facetCompleteness",
        "projectRef",
        "modelRef",
        "sourceCreatedAt",
        "sourceUpdatedAt",
        "privacyScope",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    const times = parseMutableTimes(object, path);
    const observedAt = requireIsoTimestamp(object.observedAt, `${path}.observedAt`);
    assertTimestampOrder(times.createdAt, observedAt, `${path}.observedAt`);
    assertTimestampOrder(observedAt, times.updatedAt, `${path}.updatedAt`);
    const projectRef = object.projectRef === undefined
        ? undefined
        : requireSha256Ref(object.projectRef, `${path}.projectRef`);
    const modelRef = object.modelRef === undefined
        ? undefined
        : requireSha256Ref(object.modelRef, `${path}.modelRef`);
    const sourceCreatedAt = object.sourceCreatedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.sourceCreatedAt, `${path}.sourceCreatedAt`);
    const sourceUpdatedAt = object.sourceUpdatedAt === undefined
        ? undefined
        : requireIsoTimestamp(object.sourceUpdatedAt, `${path}.sourceUpdatedAt`);
    if (sourceCreatedAt !== undefined && sourceUpdatedAt !== undefined) {
        assertTimestampOrder(sourceCreatedAt, sourceUpdatedAt, `${path}.sourceUpdatedAt`);
    }
    if (sourceCreatedAt !== undefined)
        assertTimestampOrder(sourceCreatedAt, observedAt, `${path}.observedAt`);
    if (sourceUpdatedAt !== undefined)
        assertTimestampOrder(sourceUpdatedAt, observedAt, `${path}.observedAt`);
    const resultExistence = requireEnum(object.resultExistence, `${path}.resultExistence`, ["observed", "not_observed", "unknown"]);
    const resultFinality = requireEnum(object.resultFinality, `${path}.resultFinality`, ["authoritative", "non_authoritative", "unknown"]);
    const status = requireEnum(object.status, `${path}.status`, ["in_progress", "interrupted", "no_result", "needs_review", "archived", "unknown"]);
    const goalAvailability = requireEnum(object.goalAvailability, `${path}.goalAvailability`, ["available", "not_observed", "unknown"]);
    const facetCompleteness = parseFacetCompleteness(object.facetCompleteness, `${path}.facetCompleteness`);
    if (resultExistence !== "observed" && resultFinality !== "unknown") {
        fail(`${path}.resultFinality`, "must be unknown unless a result was observed");
    }
    if (facetCompleteness.result === "unsupported"
        && (resultExistence !== "unknown" || resultFinality !== "unknown")) {
        fail(`${path}.facetCompleteness.result`, "unsupported result facets require unknown existence and finality");
    }
    if (facetCompleteness.result === "complete" && resultExistence === "unknown") {
        fail(`${path}.resultExistence`, "a complete result facet cannot have unknown existence");
    }
    if (facetCompleteness.result === "complete"
        && resultExistence === "observed"
        && resultFinality === "unknown") {
        fail(`${path}.resultFinality`, "an observed complete result facet requires known finality");
    }
    if (status === "no_result"
        && (resultExistence !== "not_observed"
            || resultFinality !== "unknown"
            || facetCompleteness.result !== "complete")) {
        fail(`${path}.status`, "no_result requires a complete not-observed result facet with unknown finality");
    }
    if (status === "needs_review" && resultExistence !== "observed") {
        fail(`${path}.resultExistence`, "needs_review requires an observed result");
    }
    if (facetCompleteness.goal === "unsupported" && goalAvailability !== "unknown") {
        fail(`${path}.goalAvailability`, "must be unknown when the goal facet is unsupported");
    }
    if (facetCompleteness.goal === "complete" && goalAvailability === "unknown") {
        fail(`${path}.goalAvailability`, "cannot be unknown when the goal facet is complete");
    }
    if (facetCompleteness.project === "unsupported" && projectRef !== undefined) {
        fail(`${path}.projectRef`, "is forbidden when the project facet is unsupported");
    }
    if (facetCompleteness.model === "unsupported" && modelRef !== undefined) {
        fail(`${path}.modelRef`, "is forbidden when the model facet is unsupported");
    }
    if (facetCompleteness.time === "unsupported"
        && (sourceCreatedAt !== undefined || sourceUpdatedAt !== undefined)) {
        fail(`${path}.facetCompleteness.time`, "unsupported time facets cannot carry source timestamps");
    }
    if (facetCompleteness.status === "unsupported" && status !== "unknown") {
        fail(`${path}.status`, "must be unknown when the status facet is unsupported");
    }
    if (facetCompleteness.status === "complete" && status === "unknown") {
        fail(`${path}.status`, "cannot be unknown when the status facet is complete");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        source: requireEnum(object.source, `${path}.source`, ["dosia", "codex", "claude-code"]),
        sessionKey: requireSha256Ref(object.sessionKey, `${path}.sessionKey`),
        fingerprint: requireSha256Ref(object.fingerprint, `${path}.fingerprint`),
        localOnly: requireLiteralTrue(object.localOnly, `${path}.localOnly`),
        observedAt,
        status,
        resultExistence,
        resultFinality,
        goalAvailability,
        facetCompleteness,
        ...(projectRef === undefined ? {} : { projectRef }),
        ...(modelRef === undefined ? {} : { modelRef }),
        ...(sourceCreatedAt === undefined ? {} : { sourceCreatedAt }),
        ...(sourceUpdatedAt === undefined ? {} : { sourceUpdatedAt }),
        privacyScope: parsePrivacyScope(object.privacyScope, `${path}.privacyScope`),
        ...times,
    };
}
export function buildContinuitySessionObservation(input) {
    return parseContinuitySessionObservation(input);
}
export function parseContinuitySessionLink(value) {
    const path = "sessionLink";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "sessionObservationId",
        "workItemId",
        "createdBy",
        "createdAt",
        "updatedAt",
        "version",
    ]);
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        sessionObservationId: requireId(object.sessionObservationId, `${path}.sessionObservationId`),
        workItemId: requireId(object.workItemId, `${path}.workItemId`),
        createdBy: requireId(object.createdBy, `${path}.createdBy`),
        ...parseMutableTimes(object, path),
    };
}
export function buildContinuitySessionLink(input) {
    return parseContinuitySessionLink(input);
}
export function parseContinuitySessionRuntime(value) {
    const path = "sessionRuntime";
    const object = requireObject(value, path);
    assertExactKeys(object, path, [
        "contractVersion",
        "id",
        "attemptId",
        "executorRef",
        "resumeRef",
        "health",
        "providerSessionKey",
        "cwd",
        "worktreeRef",
        "capabilitySnapshotRef",
        "createdAt",
        "updatedAt",
        "lastTransition",
        "version",
    ]);
    const health = requireEnum(object.health, `${path}.health`, CONTINUITY_SESSION_RUNTIME_HEALTH_VALUES);
    const times = parseMutableTimes(object, path);
    const attemptId = object.attemptId === undefined ? undefined : requireId(object.attemptId, `${path}.attemptId`);
    const resumeRef = optionalReference(object.resumeRef, `${path}.resumeRef`);
    const cwd = object.cwd === undefined ? undefined : requireAbsolutePath(object.cwd, `${path}.cwd`);
    const worktreeRef = object.worktreeRef === undefined
        ? undefined
        : requireAbsolutePath(object.worktreeRef, `${path}.worktreeRef`);
    const capabilitySnapshotRef = optionalReference(object.capabilitySnapshotRef, `${path}.capabilitySnapshotRef`);
    const lastTransition = optionalTransitionMarker(object.lastTransition, `${path}.lastTransition`);
    if (lastTransition !== undefined && lastTransition.toStatus !== health) {
        fail(`${path}.lastTransition.toStatus`, "must equal the current Runtime health");
    }
    return {
        contractVersion: requireContractVersion(object.contractVersion, `${path}.contractVersion`),
        id: requireId(object.id, `${path}.id`),
        ...(attemptId === undefined ? {} : { attemptId }),
        executorRef: parseContinuityExecutorRef(object.executorRef),
        ...(resumeRef === undefined ? {} : { resumeRef }),
        health,
        providerSessionKey: requireId(object.providerSessionKey, `${path}.providerSessionKey`),
        ...(cwd === undefined ? {} : { cwd }),
        ...(worktreeRef === undefined ? {} : { worktreeRef }),
        ...(capabilitySnapshotRef === undefined ? {} : { capabilitySnapshotRef }),
        ...times,
        ...(lastTransition === undefined ? {} : { lastTransition }),
    };
}
export function buildContinuitySessionRuntime(input) {
    const parsed = parseContinuitySessionRuntime(input);
    if (parsed.health !== "launching") {
        fail("sessionRuntime.health", "first-create Runtime must start in launching");
    }
    if (parsed.lastTransition !== undefined) {
        fail("sessionRuntime.lastTransition", "first-create Runtime must not carry a transition marker");
    }
    if (parsed.version !== 1) {
        fail("sessionRuntime.version", "first-create Runtime version must be 1");
    }
    return parsed;
}
function rejectedRecord(status, record, reason) {
    return { status, record, reason };
}
function isExactRetry(marker, eventId, fingerprint, toStatus) {
    return marker?.eventId === eventId
        && marker.fingerprint === fingerprint
        && marker.toStatus === toStatus;
}
function reusesIdempotencyElement(marker, eventId, fingerprint) {
    return marker !== undefined && (marker.eventId === eventId || marker.fingerprint === fingerprint);
}
function transitionTimeIsMonotonic(updatedAt, at) {
    const milliseconds = Date.parse(at);
    return Number.isFinite(milliseconds)
        && new Date(milliseconds).toISOString() === at
        && milliseconds >= Date.parse(updatedAt);
}
function executorRefsEqual(left, right) {
    return left.id === right.id && left.kind === right.kind && left.contractVersion === right.contractVersion;
}
/**
 * continuity 指纹专属的载荷归一化：数组里的 `undefined` 记成 `null`（占位但不丢位置），
 * 对象里的 `undefined` 键整个剔除。这是 continuity 指纹的历史语义（调用方载荷天然带
 * 可选字段），与通用的 {@link canonicalJsonDigest}（对 `undefined` 直接抛错）是两回事，
 * 故不随摘要算子一起搬到 `canonical-digest.ts`——那边没有「这份数据允许缺字段」的上下文。
 */
function normalizeCanonicalPayload(value) {
    if (Array.isArray(value)) {
        return Array.from(value, (item) => item === undefined ? null : normalizeCanonicalPayload(item));
    }
    if (typeof value === "object" && value !== null) {
        const normalized = {};
        for (const [key, item] of Object.entries(value)) {
            if (item !== undefined)
                normalized[key] = normalizeCanonicalPayload(item);
        }
        return normalized;
    }
    return value;
}
function fingerprintCanonicalPayload(payload) {
    return canonicalJsonDigest(normalizeCanonicalPayload(payload));
}
const WORK_ITEM_TRANSITIONS = {
    backlog: ["ready", "cancelled"],
    ready: ["backlog", "in_progress", "cancelled"],
    in_progress: ["ready", "waiting", "needs_review", "cancelled"],
    waiting: ["ready", "in_progress", "needs_review", "cancelled"],
    needs_review: ["ready", "in_progress", "waiting", "done", "cancelled"],
    done: [],
    cancelled: [],
};
function fingerprintWorkItemTransition(input) {
    return fingerprintCanonicalPayload({
        acceptance: input.acceptance ?? null,
        actor: { id: input.actor.id, kind: input.actor.kind },
        at: input.at,
        expectedVersion: input.expectedVersion,
        kind: "work_item_transition",
        toStatus: input.toStatus,
        workItemId: input.workItem.id,
    });
}
export function transitionContinuityWorkItem(input) {
    const { workItem, toStatus, eventId } = input;
    // Parse cross-object input before relation or digest checks so malformed
    // sparse/unknown fields cannot masquerade as an exact retry.
    let normalizedAcceptance;
    if (toStatus !== "done" && input.acceptance !== undefined) {
        return rejectedRecord("invalid_transition", workItem, "Acceptance is only valid for a done transition");
    }
    if (toStatus === "done") {
        if (input.acceptance === undefined) {
            return rejectedRecord("invalid_transition", workItem, "done requires an Acceptance");
        }
        try {
            normalizedAcceptance = parseContinuityAcceptance(input.acceptance);
        }
        catch (error) {
            return rejectedRecord("invalid_transition", workItem, error instanceof Error ? error.message : "invalid Acceptance payload");
        }
        const acceptance = normalizedAcceptance;
        if (acceptance.workItemId !== workItem.id
            || workItem.activeAttemptId === undefined
            || acceptance.attemptId !== workItem.activeAttemptId
            || (workItem.acceptanceId !== undefined && acceptance.id !== workItem.acceptanceId)) {
            return rejectedRecord("relation_mismatch", workItem, "Acceptance does not bind the active Work Item Attempt");
        }
        if (acceptance.decision !== "accept") {
            return rejectedRecord("invalid_transition", workItem, "done requires an accept decision");
        }
        if (input.actor.kind !== "human"
            || acceptance.actor.kind !== "human"
            || input.actor.id !== acceptance.actor.id) {
            return rejectedRecord("actor_forbidden", workItem, "the same human actor must own Acceptance and completion");
        }
    }
    if (toStatus === "cancelled" && input.actor.kind !== "human") {
        return rejectedRecord("actor_forbidden", workItem, "only a human actor may cancel a Work Item");
    }
    const normalizedInput = normalizedAcceptance === undefined
        ? input
        : { ...input, acceptance: normalizedAcceptance };
    const fingerprint = fingerprintWorkItemTransition(normalizedInput);
    // Priority after immutable validation: exact retry, reused-key conflict,
    // stale CAS, terminal state, then first-transition time/state validation.
    if (isExactRetry(workItem.lastTransition, eventId, fingerprint, toStatus)) {
        return { status: "idempotent", record: workItem };
    }
    if (reusesIdempotencyElement(workItem.lastTransition, eventId, fingerprint)) {
        return rejectedRecord(workItem.status === "done" || workItem.status === "cancelled" ? "terminal_conflict" : "invalid_transition", workItem, "event id or fingerprint was reused with a different canonical payload");
    }
    if (input.expectedVersion !== workItem.version) {
        return rejectedRecord("stale_version", workItem, "expected Work Item version does not match");
    }
    if (workItem.status === "done" || workItem.status === "cancelled") {
        return rejectedRecord("terminal_conflict", workItem, "terminal Work Item cannot transition");
    }
    if (!transitionTimeIsMonotonic(workItem.updatedAt, input.at)) {
        return rejectedRecord("invalid_transition", workItem, "transition time must be canonical and monotonic");
    }
    if (toStatus === workItem.status || !WORK_ITEM_TRANSITIONS[workItem.status].includes(toStatus)) {
        return rejectedRecord("invalid_transition", workItem, "Work Item transition is not allowed");
    }
    if (toStatus === "done") {
        const acceptance = normalizedAcceptance;
        if (acceptance === undefined
            || Date.parse(acceptance.updatedAt) < Date.parse(workItem.updatedAt)
            || Date.parse(acceptance.updatedAt) > Date.parse(input.at)) {
            return rejectedRecord("invalid_transition", workItem, "Acceptance must occur between Work Item update and transition");
        }
    }
    const marker = {
        eventId,
        fingerprint,
        toStatus,
    };
    try {
        return {
            status: "applied",
            record: parseContinuityWorkItem({
                ...workItem,
                status: toStatus,
                updatedAt: input.at,
                ...(toStatus === "done" || toStatus === "cancelled" ? { completedAt: input.at } : {}),
                ...(toStatus === "done" ? { acceptanceId: normalizedAcceptance?.id } : {}),
                lastTransition: marker,
                version: workItem.version + 1,
            }),
        };
    }
    catch (error) {
        return rejectedRecord("invalid_transition", workItem, error instanceof Error ? error.message : "invalid Work Item transition payload");
    }
}
const ATTEMPT_TRANSITIONS = {
    queued: ["running", "waiting_auth", "waiting_approval", "interrupted", "succeeded", "failed", "cancelled", "timed_out"],
    running: ["waiting_auth", "waiting_approval", "interrupted", "succeeded", "failed", "cancelled", "timed_out"],
    waiting_auth: ["queued", "running", "interrupted", "failed", "cancelled", "timed_out"],
    waiting_approval: ["queued", "running", "interrupted", "failed", "cancelled", "timed_out"],
    interrupted: ["queued", "running", "failed", "cancelled", "timed_out"],
    succeeded: [],
    failed: [],
    cancelled: [],
    timed_out: [],
};
const TERMINAL_ATTEMPT_STATUSES = new Set([
    "succeeded",
    "failed",
    "cancelled",
    "timed_out",
]);
const INITIAL_TRANSFER_SOURCE_STATUSES = new Set([
    "queued",
    "waiting_auth",
    "waiting_approval",
    "interrupted",
]);
const SAFE_ACCEPTED_RETRY_SOURCE_STATUSES = new Set([
    ...INITIAL_TRANSFER_SOURCE_STATUSES,
    "failed",
    "cancelled",
    "timed_out",
]);
function fingerprintAttemptTransition(input) {
    return fingerprintCanonicalPayload({
        at: input.at,
        attemptId: input.attempt.id,
        checkpointRef: input.checkpointRef ?? null,
        expectedVersion: input.expectedVersion,
        failure: input.failure ?? null,
        kind: "attempt_transition",
        resultSummary: input.resultSummary ?? null,
        resumeRef: input.resumeRef ?? null,
        toStatus: input.toStatus,
    });
}
export function transitionContinuityAttempt(input) {
    const { attempt, toStatus, eventId } = input;
    if (input.resultSummary !== undefined && toStatus !== "succeeded") {
        return rejectedRecord("invalid_transition", attempt, "resultSummary is only valid for succeeded");
    }
    if (input.failure !== undefined && toStatus !== "failed") {
        return rejectedRecord("invalid_transition", attempt, "failure is only valid for failed");
    }
    const fingerprint = fingerprintAttemptTransition(input);
    if (isExactRetry(attempt.lastTransition, eventId, fingerprint, toStatus)) {
        return { status: "idempotent", record: attempt };
    }
    if (reusesIdempotencyElement(attempt.lastTransition, eventId, fingerprint)) {
        return rejectedRecord(TERMINAL_ATTEMPT_STATUSES.has(attempt.status) ? "terminal_conflict" : "invalid_transition", attempt, "event id or fingerprint was reused with a different canonical payload");
    }
    if (input.expectedVersion !== attempt.version) {
        return rejectedRecord("stale_version", attempt, "expected Attempt version does not match");
    }
    if (TERMINAL_ATTEMPT_STATUSES.has(attempt.status)) {
        return rejectedRecord("terminal_conflict", attempt, "terminal Attempt cannot transition");
    }
    if (!transitionTimeIsMonotonic(attempt.updatedAt, input.at)) {
        return rejectedRecord("invalid_transition", attempt, "transition time must be canonical and monotonic");
    }
    if (toStatus === attempt.status || !ATTEMPT_TRANSITIONS[attempt.status].includes(toStatus)) {
        return rejectedRecord("invalid_transition", attempt, "Attempt transition is not allowed");
    }
    const marker = {
        eventId,
        fingerprint,
        toStatus,
    };
    const terminal = TERMINAL_ATTEMPT_STATUSES.has(toStatus);
    const requiresStartedAt = toStatus === "running" || toStatus === "interrupted" || terminal;
    const { endedAt: _endedAt, failure: _failure, lastTransition: _lastTransition, recoveryEvidenceId: _recoveryEvidenceId, resultSummary: _resultSummary, ...cleanAttempt } = attempt;
    try {
        return {
            status: "applied",
            record: parseContinuityAttempt({
                ...cleanAttempt,
                status: toStatus,
                updatedAt: input.at,
                ...(requiresStartedAt && attempt.startedAt === undefined ? { startedAt: input.at } : {}),
                ...(terminal ? { endedAt: input.at } : {}),
                ...(toStatus === "succeeded" && input.resultSummary !== undefined
                    ? { resultSummary: input.resultSummary }
                    : {}),
                ...(toStatus === "failed" && input.failure !== undefined ? { failure: input.failure } : {}),
                ...(input.resumeRef === undefined ? {} : { resumeRef: input.resumeRef }),
                ...(input.checkpointRef === undefined ? {} : { checkpointRef: input.checkpointRef }),
                lastTransition: marker,
                version: attempt.version + 1,
            }),
        };
    }
    catch (error) {
        return rejectedRecord("invalid_transition", attempt, error instanceof Error ? error.message : "invalid Attempt transition payload");
    }
}
const SESSION_RUNTIME_HEALTH_TRANSITIONS = {
    launching: ["working", "idle", "waiting_auth", "waiting_approval", "hibernated", "dead", "unreachable"],
    working: ["idle", "waiting_auth", "waiting_approval", "hibernated", "dead", "unreachable"],
    idle: ["working", "hibernated", "dead", "unreachable"],
    waiting_auth: ["working", "dead", "unreachable"],
    waiting_approval: ["working", "dead", "unreachable"],
    hibernated: ["idle", "working", "dead", "unreachable"],
    dead: [],
    unreachable: [],
};
const TERMINAL_SESSION_RUNTIME_HEALTH = new Set([
    "dead",
    "unreachable",
]);
function fingerprintSessionRuntimeHealthTransition(input) {
    return fingerprintCanonicalPayload({
        at: input.at,
        expectedVersion: input.expectedVersion,
        kind: "session_runtime_health_transition",
        runtimeId: input.runtime.id,
        toHealth: input.toHealth,
    });
}
export function transitionContinuitySessionRuntimeHealth(input) {
    const { runtime, toHealth, eventId } = input;
    const fingerprint = fingerprintSessionRuntimeHealthTransition(input);
    if (isExactRetry(runtime.lastTransition, eventId, fingerprint, toHealth)) {
        return { status: "idempotent", record: runtime };
    }
    if (reusesIdempotencyElement(runtime.lastTransition, eventId, fingerprint)) {
        return rejectedRecord(TERMINAL_SESSION_RUNTIME_HEALTH.has(runtime.health) ? "terminal_conflict" : "invalid_transition", runtime, "event id or fingerprint was reused with a different canonical payload");
    }
    if (input.expectedVersion !== runtime.version) {
        return rejectedRecord("stale_version", runtime, "expected Runtime version does not match");
    }
    if (toHealth === runtime.health) {
        return { status: "idempotent", record: runtime };
    }
    if (TERMINAL_SESSION_RUNTIME_HEALTH.has(runtime.health)) {
        return rejectedRecord("terminal_conflict", runtime, "terminal Runtime health cannot transition");
    }
    if (!transitionTimeIsMonotonic(runtime.updatedAt, input.at)) {
        return rejectedRecord("invalid_transition", runtime, "transition time must be canonical and monotonic");
    }
    if (!SESSION_RUNTIME_HEALTH_TRANSITIONS[runtime.health].includes(toHealth)) {
        return rejectedRecord("invalid_transition", runtime, "Runtime health transition is not allowed");
    }
    const marker = {
        eventId,
        fingerprint,
        toStatus: toHealth,
    };
    const { lastTransition: _lastTransition, ...cleanRuntime } = runtime;
    try {
        return {
            status: "applied",
            record: parseContinuitySessionRuntime({
                ...cleanRuntime,
                health: toHealth,
                updatedAt: input.at,
                lastTransition: marker,
                version: runtime.version + 1,
            }),
        };
    }
    catch (error) {
        return rejectedRecord("invalid_transition", runtime, error instanceof Error ? error.message : "invalid Runtime health transition payload");
    }
}
function fingerprintAttemptRecovery(input, evidence, resultSummary) {
    return fingerprintCanonicalPayload({
        at: input.at,
        attemptId: input.attempt.id,
        evidence,
        expectedVersion: input.expectedVersion,
        kind: "attempt_recovery",
        resultSummary: resultSummary ?? null,
    });
}
export function recoverContinuityAttemptFromEvidence(input) {
    const { attempt } = input;
    let evidence;
    let resultSummary;
    try {
        evidence = parseContinuityEvidence(input.evidence);
        resultSummary = optionalString(input.resultSummary, "attemptRecovery.resultSummary");
    }
    catch (error) {
        return rejectedRecord("invalid_transition", attempt, error instanceof Error ? error.message : "invalid Attempt recovery payload");
    }
    if (evidence.workItemId !== attempt.workItemId || evidence.attemptId !== attempt.id) {
        return rejectedRecord("relation_mismatch", attempt, "Recovery Evidence does not bind the target Attempt");
    }
    if (attempt.status === "succeeded"
        && attempt.recoveryEvidenceId !== undefined
        && attempt.recoveryEvidenceId !== evidence.id) {
        return rejectedRecord("relation_mismatch", attempt, "Recovered Attempt is bound to different Evidence");
    }
    if (evidence.authority !== "authoritative" || evidence.outcome !== "passed") {
        return rejectedRecord("invalid_transition", attempt, "Recovery requires authoritative passed Evidence");
    }
    const fingerprint = fingerprintAttemptRecovery(input, evidence, resultSummary);
    if (isExactRetry(attempt.lastTransition, input.eventId, fingerprint, "succeeded")) {
        if (attempt.status !== "succeeded" || attempt.recoveryEvidenceId !== evidence.id) {
            return rejectedRecord("relation_mismatch", attempt, "Exact recovery retry does not match the persisted Evidence binding");
        }
        return { status: "idempotent", record: attempt };
    }
    if (reusesIdempotencyElement(attempt.lastTransition, input.eventId, fingerprint)) {
        return rejectedRecord(TERMINAL_ATTEMPT_STATUSES.has(attempt.status) ? "terminal_conflict" : "invalid_transition", attempt, "recovery event id or fingerprint was reused with a different canonical payload");
    }
    if (input.expectedVersion !== attempt.version) {
        return rejectedRecord("stale_version", attempt, "expected Attempt version does not match");
    }
    if (TERMINAL_ATTEMPT_STATUSES.has(attempt.status)) {
        return rejectedRecord("terminal_conflict", attempt, "terminal Attempt cannot recover");
    }
    if (attempt.status !== "interrupted") {
        return rejectedRecord("invalid_transition", attempt, "only an interrupted Attempt may recover");
    }
    if (!transitionTimeIsMonotonic(attempt.updatedAt, input.at)) {
        return rejectedRecord("invalid_transition", attempt, "recovery time must be canonical and monotonic");
    }
    const evidenceTime = Date.parse(evidence.updatedAt);
    if (evidenceTime < Date.parse(attempt.updatedAt) || evidenceTime > Date.parse(input.at)) {
        return rejectedRecord("invalid_transition", attempt, "Recovery Evidence must occur between interruption and recovery");
    }
    const marker = {
        eventId: input.eventId,
        fingerprint,
        toStatus: "succeeded",
    };
    const { endedAt: _endedAt, failure: _failure, lastTransition: _lastTransition, recoveryEvidenceId: _recoveryEvidenceId, resultSummary: _resultSummary, ...cleanAttempt } = attempt;
    try {
        return {
            status: "applied",
            record: parseContinuityAttempt({
                ...cleanAttempt,
                status: "succeeded",
                updatedAt: input.at,
                endedAt: input.at,
                ...(resultSummary === undefined ? {} : { resultSummary }),
                recoveryEvidenceId: evidence.id,
                lastTransition: marker,
                version: attempt.version + 1,
            }),
        };
    }
    catch (error) {
        return rejectedRecord("invalid_transition", attempt, error instanceof Error ? error.message : "invalid Attempt recovery payload");
    }
}
function fingerprintTransferTransition(input) {
    const accepted = input.toStatus === "accepted";
    return fingerprintCanonicalPayload({
        at: input.at,
        contextPackId: accepted ? input.contextPack?.id ?? null : null,
        expectedContextPackVersion: accepted ? input.expectedContextPackVersion ?? null : null,
        expectedSourceAttemptVersion: input.expectedSourceAttemptVersion,
        expectedTargetAttemptVersion: accepted ? input.expectedTargetAttemptVersion ?? null : null,
        expectedTransferVersion: input.expectedTransferVersion,
        expectedWorkItemVersion: input.expectedWorkItemVersion,
        kind: "transfer_transition",
        reason: accepted ? null : input.reason ?? null,
        targetAttemptId: accepted ? input.targetAttempt?.id ?? null : null,
        toStatus: input.toStatus,
        transferId: input.transfer.id,
    });
}
function rejectedTransfer(input, status, reason) {
    return {
        status,
        transfer: input.transfer,
        sourceAttempt: input.sourceAttempt,
        workItem: input.workItem,
        reason,
    };
}
export function transitionContinuityTransfer(input) {
    const { transfer, sourceAttempt, workItem, toStatus, eventId } = input;
    if (transfer.workItemId !== workItem.id
        || transfer.sourceAttemptId !== sourceAttempt.id
        || sourceAttempt.workItemId !== workItem.id) {
        return rejectedTransfer(input, "relation_mismatch", "Transfer source records do not describe one Work Item Attempt");
    }
    let fingerprint;
    if (toStatus === "accepted") {
        const targetAttempt = input.targetAttempt;
        const contextPack = input.contextPack;
        if (input.reason !== undefined) {
            return rejectedTransfer(input, "invalid_transition", "accepted Transfer must not include a reason");
        }
        if (targetAttempt === undefined
            || contextPack === undefined
            || input.expectedTargetAttemptVersion === undefined
            || input.expectedContextPackVersion === undefined) {
            return rejectedTransfer(input, "invalid_transition", "accepted Transfer requires target Attempt and Context Pack snapshots");
        }
        if (targetAttempt.id === sourceAttempt.id
            || targetAttempt.workItemId !== workItem.id
            || !executorRefsEqual(targetAttempt.executorRef, transfer.targetExecutorRef)
            || transfer.contextPackId !== contextPack.id
            || contextPack.workItemId !== workItem.id
            || contextPack.attemptId !== sourceAttempt.id
            || !executorRefsEqual(contextPack.sourceExecutorRef, sourceAttempt.executorRef)
            || !executorRefsEqual(contextPack.targetExecutorRef, transfer.targetExecutorRef)) {
            return rejectedTransfer(input, "relation_mismatch", "target Attempt or Context Pack does not match Transfer relations");
        }
        if (transfer.status === "accepted"
            && (transfer.targetAttemptId !== targetAttempt.id || workItem.activeAttemptId !== targetAttempt.id)) {
            return rejectedTransfer(input, "relation_mismatch", "accepted Transfer retry must bind the persisted active target Attempt");
        }
        if (transfer.status === "accepted"
            && !SAFE_ACCEPTED_RETRY_SOURCE_STATUSES.has(sourceAttempt.status)) {
            return rejectedTransfer(input, "invalid_transition", "accepted Transfer retry requires a safely paused or terminal source Attempt");
        }
        fingerprint = fingerprintTransferTransition(input);
    }
    else {
        if (input.targetAttempt !== undefined
            || input.contextPack !== undefined
            || input.expectedTargetAttemptVersion !== undefined
            || input.expectedContextPackVersion !== undefined) {
            return rejectedTransfer(input, "invalid_transition", "only accepted Transfer may include target snapshots");
        }
        if (input.reason === undefined) {
            return rejectedTransfer(input, "invalid_transition", `${toStatus} Transfer requires a reason`);
        }
        fingerprint = fingerprintTransferTransition(input);
    }
    if (isExactRetry(transfer.lastTransition, eventId, fingerprint, toStatus)) {
        return { status: "idempotent", transfer, sourceAttempt, workItem };
    }
    if (reusesIdempotencyElement(transfer.lastTransition, eventId, fingerprint)) {
        return rejectedTransfer(input, transfer.status === "pending" ? "invalid_transition" : "terminal_conflict", "event id or fingerprint was reused with a different canonical payload");
    }
    if (transfer.status !== "pending") {
        return rejectedTransfer(input, "terminal_conflict", "terminal Transfer cannot transition");
    }
    if (input.expectedTransferVersion !== transfer.version
        || input.expectedWorkItemVersion !== workItem.version
        || input.expectedSourceAttemptVersion !== sourceAttempt.version) {
        return rejectedTransfer(input, "stale_version", "expected Transfer, Work Item, or source Attempt version does not match");
    }
    if (workItem.status === "done" || workItem.status === "cancelled" || TERMINAL_ATTEMPT_STATUSES.has(sourceAttempt.status)) {
        return rejectedTransfer(input, "invalid_transition", "Transfer requires nonterminal Work Item and source Attempt");
    }
    if (workItem.activeAttemptId !== sourceAttempt.id) {
        return rejectedTransfer(input, "relation_mismatch", "pending Transfer requires the active source Attempt");
    }
    if (toStatus === "accepted") {
        if (!INITIAL_TRANSFER_SOURCE_STATUSES.has(sourceAttempt.status)) {
            return rejectedTransfer(input, "invalid_transition", "source Attempt must be safely paused before Transfer acceptance");
        }
        if (input.targetAttempt.status !== "queued") {
            return rejectedTransfer(input, "invalid_transition", "target Attempt must be queued before Transfer acceptance");
        }
        if (input.expectedTargetAttemptVersion !== input.targetAttempt.version
            || input.expectedContextPackVersion !== input.contextPack.version) {
            return rejectedTransfer(input, "stale_version", "expected target Attempt or Context Pack version does not match");
        }
    }
    if (!transitionTimeIsMonotonic(transfer.updatedAt, input.at)) {
        return rejectedTransfer(input, "invalid_transition", "transition time must be canonical and monotonic");
    }
    const marker = {
        eventId,
        fingerprint,
        toStatus,
    };
    let nextTransfer;
    try {
        nextTransfer = parseContinuityTransfer({
            ...transfer,
            status: toStatus,
            updatedAt: input.at,
            ...(toStatus === "accepted" ? { targetAttemptId: input.targetAttempt.id } : { reason: input.reason }),
            lastTransition: marker,
            version: transfer.version + 1,
        });
    }
    catch (error) {
        return rejectedTransfer(input, "invalid_transition", error instanceof Error ? error.message : "invalid Transfer transition payload");
    }
    if (toStatus === "accepted") {
        return {
            status: "applied",
            transfer: nextTransfer,
            sourceAttempt,
            workItem,
            repositoryIntent: {
                kind: "accept_transfer",
                transfer: nextTransfer,
                workItemId: workItem.id,
                sourceAttemptId: sourceAttempt.id,
                targetAttemptId: input.targetAttempt.id,
                contextPackId: input.contextPack.id,
                expectedTransferVersion: input.expectedTransferVersion,
                expectedWorkItemVersion: input.expectedWorkItemVersion,
                expectedSourceAttemptVersion: input.expectedSourceAttemptVersion,
                expectedTargetAttemptVersion: input.expectedTargetAttemptVersion,
                expectedContextPackVersion: input.expectedContextPackVersion,
            },
        };
    }
    return {
        status: "applied",
        transfer: nextTransfer,
        sourceAttempt,
        workItem,
        repositoryIntent: {
            kind: "update_transfer",
            transfer: nextTransfer,
            workItemId: workItem.id,
            sourceAttemptId: sourceAttempt.id,
            expectedTransferVersion: input.expectedTransferVersion,
            expectedWorkItemVersion: input.expectedWorkItemVersion,
            expectedSourceAttemptVersion: input.expectedSourceAttemptVersion,
        },
    };
}
export function mapCloudTaskV2StatusToContinuityAttempt(sourceStatus) {
    switch (sourceStatus) {
        case "unknown":
            return {
                sourceStatus,
                mappedStatus: null,
                lossy: true,
                manualReview: true,
                allowReplaySideEffect: false,
            };
        case "skipped":
            return {
                sourceStatus,
                mappedStatus: null,
                lossy: true,
                allowReplaySideEffect: false,
                reason: "scheduler_skip_has_no_attempt_terminal_equivalent",
            };
        case "pending":
        case "queued":
            return { sourceStatus, mappedStatus: "queued", lossy: sourceStatus === "pending", allowReplaySideEffect: false };
        case "running":
            return { sourceStatus, mappedStatus: "running", lossy: false, allowReplaySideEffect: false };
        case "blocked":
        case "leased":
            return {
                sourceStatus,
                mappedStatus: null,
                lossy: true,
                manualReview: true,
                allowReplaySideEffect: false,
                reason: sourceStatus === "blocked"
                    ? "scheduler_block_reason_has_no_authoritative_attempt_state"
                    : "scheduler_lease_does_not_prove_attempt_started",
            };
        case "succeeded":
        case "failed":
        case "cancelled":
            return { sourceStatus, mappedStatus: sourceStatus, lossy: false, allowReplaySideEffect: false };
    }
}
export function mapEnterpriseTaskDefinitionStatusToContinuityWorkItem(sourceStatus) {
    return {
        sourceStatus,
        mappedStatus: null,
        lossy: true,
        reason: "definition_lifecycle_is_not_work_item_execution",
    };
}
//# sourceMappingURL=continuity-domain.js.map